// src/hooks/useAssistantSession.js
import { useState, useRef, useEffect, useContext } from "react";
import { useToast } from "@chakra-ui/react";
import { GlobalContext } from "../../../component/GlobalContext";
import usePeerConnection from "./usePeerConnection";

export const useAssistantSession = () => {
    const toast = useToast();
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [dataChannel, setDataChannel] = useState(null);
    const [events, setEvents] = useState([]);
    const [sessionError, setSessionError] = useState(null);

    const audioElement = useRef(null);
    const { createSession, runCommands } = useContext(GlobalContext);

    const { peerConnection, createPeerConnection, closePeerConnection } =
        usePeerConnection(audioElement);

    const [messages, setMessages] = useState([]);
    const [currentAssistantMessage, setCurrentAssistantMessage] = useState("");
    const [currentUserMessage, setCurrentUserMessage] = useState("");
    const userCompletedTranscriptBuffer = useRef(""); // Acumula los completados
    const userCompletedTimeoutRef = useRef(null); // Timeout para detectar silencio
    const assistantTimeoutRef = useRef(null);
    const assistantBufferRef = useRef("");

    const updateAssistantMessage = () => {
        if (assistantTimeoutRef.current) clearTimeout(assistantTimeoutRef.current);
        assistantTimeoutRef.current = setTimeout(() => {
            setCurrentAssistantMessage(assistantBufferRef.current);
        }, 50);
    };

    const handleTranscriptDelta = (event) => {
        assistantBufferRef.current += event.delta;
        updateAssistantMessage();
    };

    const handleUserTranscriptionDelta = (event) => {
        setCurrentUserMessage((prev) => (prev + " " + event.delta).trim());
    };

    const handleInputAudioCompleted = (event) => {
        userCompletedTranscriptBuffer.current += event.transcript + " ";

        setCurrentUserMessage((prev) => (prev + " " + event.transcript).trim());

        if (userCompletedTimeoutRef.current) {
            clearTimeout(userCompletedTimeoutRef.current);
        }

        userCompletedTimeoutRef.current = setTimeout(() => {
            const finalTranscript = userCompletedTranscriptBuffer.current.trim();
            if (finalTranscript) {
                setMessages((prev) => [
                    ...prev,
                    {
                        key: crypto.randomUUID(),
                        role: "user",
                        content: finalTranscript,
                    },
                ]);
            }

            userCompletedTranscriptBuffer.current = "";
            setCurrentUserMessage("");
        }, 1500);
    };

    const handleFunctionCall = async (output, event) => {
        const { commands } = JSON.parse(output.arguments);
        const message_id = crypto.randomUUID();

        const message = {
            key: message_id,
            role: "tools",
            type: "function_call",
            name: "execute_fql_command",
            content: { commands },
        };

        setMessages((prev) => [...prev, message]);

        try {
            const result = await runCommands({ commands });

            sendClientEvent({
                type: "conversation.item.create",
                item: {
                    type: "function_call_output",
                    call_id: output.call_id,
                    output: JSON.stringify(result),
                },
            });

            setMessages((prev) => [
                ...prev,
                {
                    role: "tools",
                    type: "function_call_output",
                    call_id: message_id,
                    name: "execute_fql_command",
                    content: { commands, result },
                },
            ]);

            sendClientEvent({
                type: "response.create",
                response: {
                    instructions:
                        "Analize the function output and give the according response to the user",
                },
            });
        } catch (err) {
            console.error("Failed to run FQL commands", err);
            // Optional: handle error with a message to the UI
        }
    };

    const handleAssistantMessage = (event) => {
        const newMessages = [];

        event.response.output.forEach((output) => {
            if (output.type === "message") {
                output.content.forEach((content) => {
                    newMessages.push({
                        key: event.event_id + content.transcript.slice(0, 8),
                        role: "assistant",
                        content: content.transcript,
                    });
                });
            }
        });

        setTimeout(() => {
            setCurrentAssistantMessage("");
            setMessages((prev) => [...prev, ...newMessages]);
        }, 100);
    };

    const processEvent = async (event) => {
        switch (event.type) {
            case "response.audio_transcript.delta":
                handleTranscriptDelta(event);
                break;

            case "conversation.item.input_audio_transcription.delta":
                handleUserTranscriptionDelta(event);
                break;

            case "conversation.item.input_audio_transcription.completed":
                handleInputAudioCompleted(event);
                break;

            case "response.done":
                assistantBufferRef.current = "";
                for (const output of event.response?.output || []) {
                    if (
                        output.type === "function_call" &&
                        output.name === "execute_fql_command"
                    ) {
                        await handleFunctionCall(output, event);
                    }
                }
                handleAssistantMessage(event);
                break;

            default:
                break;
        }

        return event;
    };

    const sendClientEvent = (message) => {
        if (dataChannel) {
            message.event_id = message.event_id || crypto.randomUUID();
            dataChannel.send(JSON.stringify(message));
            processEvent(message);
            setEvents((prev) => [message, ...prev]);
        } else {
            console.error(
                "Failed to send message - no data channel available",
                message
            );
        }
    };

    const sendTextMessage = (message) => {
        const event = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: message,
                    },
                ],
            },
        };
        sendClientEvent(event);
        sendClientEvent({ type: "response.create" });
    };

    const copyChat = ({ showDialog = true, showCommands = true }) => {
        const chatContent = JSON.stringify(
            messages
                .filter(({ role, type, key }) => {
                    if (role === "tools" && type === "function_call") {
                        if (messages.find((message) => key === message.call_id))
                            return false;
                    }
                    return role === "tools" ? showCommands : showDialog;
                })
                .map((msg) => ({
                    key: msg.key,
                    call_id: msg.call_id,
                    role: msg.role,
                    type: msg.type,
                    name: msg.name,
                    content: msg.content,
                })),
            null,
            "\t"
        );

        navigator.clipboard
            .writeText(chatContent)
            .then(() => {
                toast({
                    title: "Copied",
                    description: "Chat messages copied to clipboard.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right",
                });
            })
            .catch((err) => {
                console.error("Failed to copy chat messages:", err);
                toast({
                    title: "Error",
                    description: "Failed to copy chat messages.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right",
                });
            });
    };

    const startSession = async () => {
        try {
            setSessionError(null);
            const data = await createSession();

            if (data.error) {
                setSessionError(data.error);
                toast({
                    title: "Error",
                    description: data.error,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right",
                });
                return;
            }

            const EPHEMERAL_KEY = data.client_secret.value;

            await createPeerConnection();

            const dc = peerConnection.current.createDataChannel("oai-events");
            setDataChannel(dc);

            const offer = await peerConnection.current.createOffer();
            await peerConnection.current.setLocalDescription(offer);

            const baseUrl = "https://api.openai.com/v1/realtime";
            const model = "gpt-4o-realtime-preview-2024-12-17";
            const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${EPHEMERAL_KEY}`,
                    "Content-Type": "application/sdp",
                },
            });

            const answer = {
                type: "answer",
                sdp: await sdpResponse.text(),
            };
            await peerConnection.current.setRemoteDescription(answer);
        } catch (error) {
            console.error(error);
        }
    };

    const stopSession = () => {
        if (dataChannel) {
            dataChannel.close();
        }
        closePeerConnection();

        setIsSessionActive(false);
        setDataChannel(null);
    };

    useEffect(() => {
        if (dataChannel) {
            dataChannel.addEventListener("message", (e) => {
                const event = JSON.parse(e.data);
                processEvent(event);
                setEvents((prev) => [event, ...prev]);
            });
            dataChannel.addEventListener("open", () => {
                setIsSessionActive(true);
                setEvents([]);
                // 👇 Add this
                sendClientEvent({
                    type: "conversation.item.create",
                    item: {
                        type: "message",
                        role: "user",
                        content: [
                            {
                                type: "input_text",
                                text: "You're live now. Please greet the user.",
                            },
                        ],
                    },
                });

                sendClientEvent({ type: "response.create" });
            });
        }
    }, [dataChannel]);

    return {
        isSessionActive,
        setIsSessionActive,
        dataChannel,
        setDataChannel,
        events,
        setEvents,
        sessionError,
        setSessionError,
        startSession,
        stopSession,
        audioElement,
        peerConnection,
        sendClientEvent,
        sendTextMessage,
        messages,
        setMessages,
        currentAssistantMessage,
        setCurrentAssistantMessage,
        currentUserMessage,
        setCurrentUserMessage,
        copyChat,
    };
};
